import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Building, List, Plus, Receipt, TrendingUp, DollarSign, CheckCircle, Clock, X } from 'lucide-react';
import { invoiceApi, companyProfileApi } from '../services/api';

// Static mock data
const getMockStats = () => {
  return {
    totalInvoices: 5,
    generated: 2,
    sent: 1,
    paid: 2,
    cancelled: 0
  };
};

const getMockRecentInvoices = () => {
  return [
    {
      invoiceId: 1,
      invoice_number: '123876',
      orderId: 'OD3455',
      customer_name: 'Hemant Mehto',
      amount: 1500,
      invoice_status: 'GENERATED',
      order_date: '2024-03-23T17:09:00'
    },
    {
      invoiceId: 2,
      invoice_number: '123877',
      orderId: 'OD3456',
      customer_name: 'Sumit Kumar Upadhyay',
      amount: 5480,
      invoice_status: 'SENT',
      order_date: '2024-03-24T10:15:00'
    },
    {
      invoiceId: 3,
      invoice_number: '123878',
      orderId: 'OD3457',
      customer_name: 'Deep Singh',
      amount: 400,
      invoice_status: 'PAID',
      order_date: '2024-03-25T14:30:00'
    }
  ];
};

function Billing() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalInvoices: 0,
    generated: 0,
    sent: 0,
    paid: 0,
    cancelled: 0
  });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [profilesCount, setProfilesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [invoices, profiles] = await Promise.all([
        invoiceApi.getAllInvoices(),
        companyProfileApi.getAllProfiles()
      ]);

      // Calculate stats
      const invoiceList = Array.isArray(invoices) ? invoices : (invoices.content || []);
      const profileList = Array.isArray(profiles) ? profiles : (profiles.content || []);

      const newStats = {
        totalInvoices: invoiceList.length,
        generated: invoiceList.filter(i => i.invoiceStatus === 'GENERATED').length,
        sent: invoiceList.filter(i => i.invoiceStatus === 'SENT').length,
        paid: invoiceList.filter(i => i.invoiceStatus === 'PAID').length,
        cancelled: invoiceList.filter(i => i.invoiceStatus === 'CANCELLED').length
      };

      setStats(newStats);
      setRecentInvoices(invoiceList.slice(0, 5)); // Top 5
      setProfilesCount(profileList.length);

    } catch (error) {
      console.error('Error fetching billing dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };


  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Billing Dashboard</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalInvoices}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Receipt className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Generated</p>
              <p className="text-2xl font-bold text-gray-900">{stats.generated}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Sent</p>
              <p className="text-2xl font-bold text-gray-900">{stats.sent}</p>
            </div>
            <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-cyan-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Paid</p>
              <p className="text-2xl font-bold text-gray-900">{stats.paid}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Cancelled</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cancelled}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <X className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions and Navigation */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Self Profile Card */}
        <div
          onClick={() => navigate('/billing/self-profile')}
          className="bg-white rounded-xl border border-gray-200 shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Self Profile</h3>
              <p className="text-sm text-gray-500">{profilesCount} profile{profilesCount !== 1 ? 's' : ''} configured</p>
            </div>
          </div>
        </div>

        {/* Billing List Card */}
        <div
          onClick={() => navigate('/billing/list')}
          className="bg-white rounded-xl border border-gray-200 shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <List className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Billing List</h3>
              <p className="text-sm text-gray-500">View all invoices</p>
            </div>
          </div>
        </div>

        {/* Add Profile Card */}
        <div
          onClick={() => navigate('/billing/add-profile')}
          className="bg-white rounded-xl border border-gray-200 shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Plus className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Add Profile</h3>
              <p className="text-sm text-gray-500">Create new company profile</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      {recentInvoices.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800">Recent Invoices</h2>
            <button
              onClick={() => navigate('/billing/list')}
              className="text-sm text-[#459B7A] hover:text-[#3a8a6a] font-medium"
            >
              View All
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Invoice #</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Order ID</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Customer</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentInvoices.map((invoice) => (
                  <tr
                    key={invoice.invoiceId || invoice.invoice_id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/billing/invoice/${invoice.invoiceId || invoice.invoice_id}`)}
                  >
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                      {invoice.invoiceNumber || invoice.invoice_number}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {invoice.orderId || invoice.order_id}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {invoice.customerName || invoice.customer_name}
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-gray-900">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${(invoice.invoiceStatus || invoice.invoice_status) === 'PAID'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : (invoice.invoiceStatus || invoice.invoice_status) === 'SENT'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : (invoice.invoiceStatus || invoice.invoice_status) === 'CANCELLED'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        }`}>
                        {invoice.invoiceStatus || invoice.invoice_status || 'GENERATED'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {formatDate(invoice.orderDate || invoice.order_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Billing;
